varying vec3 vWorldPosition;
varying vec3 vColor;
varying float vWaveIntensity;
varying float vDistanceFromCenter; // Distance from wave center for appearance effect

// Spherical waves uniforms
uniform float uTime;
uniform vec3 uCenter;
uniform float uWavesAmplitude; // Wave width (1-10) - controls how many points are affected
uniform float uWavePeriod; // Number of simultaneous waves (1-10)
uniform float uWavesSpeed; // Wave propagation speed (units per second)
uniform float uMaxDistance; // Maximum distance from center
uniform float uMinDistance; // Minimum distance from center where waves start
uniform float uDisplacementAxis; // 0=x, 1=y, 2=z
uniform float uDisplacement; // Displacement amount (0-10)
uniform float uPointSize;
// Size attenuation: 0 = constant pixel size (default, prior behavior),
// 1 = scale by 1/-viewZ (closer = bigger, like a billboard at world scale).
uniform float uSizeAttenuation;
// Multiplier applied when attenuation is on. Tunes the on-screen size at
// reference depth: gl_PointSize = uPointSize * uAttenuationScale / -viewZ.
uniform float uAttenuationScale;

void main() {
    // Get world position for fragment shader calculations (needed for reveal effect)
    vec4 worldPosition = modelMatrix * vec4(position, 1.0);
    vec3 worldPos = worldPosition.xyz;
    // glTF 2.0 spec stores vertex colors in linear color space (verified for
    // our 6 preview-GLBs: Uint8Array normalized, no vertexColorsSpace flag).
    // Pass through as-is — the renderer (outputColorSpace = SRGBColorSpace)
    // does Linear→sRGB encoding on output. donmccurdy's gltf-viewer follows
    // the same path and serves as our reference look.
    vColor = color.rgb;
    
    // Calculate distance from center (local coordinates)
    // uCenter is the wave origin in local model coordinates
    vec3 offsetFromCenter = position - uCenter;
    float distance = length(offsetFromCenter.xz);
    
    // Use absolute speed value directly (units per second)
    // No adaptive scaling - user controls speed directly
    float normalizedSpeed = uWavesSpeed;
    
    // Calculate wave interval: how far apart waves start (in distance units)
    // More waves (higher period) = smaller interval between wave starts
    // If period = 5, we want up to 5 waves simultaneously, so interval = maxDistance / 5
    float waveInterval = uMaxDistance / max(uWavePeriod, 1.0);
    
    // Calculate time interval: how often new waves start (in seconds)
    // Time for one wave to travel waveInterval distance
    float timeInterval = waveInterval / normalizedSpeed;
    
    // Calculate current wave number (most recent wave that started)
    float currentWaveNumber = floor(uTime / timeInterval);
    
    // Check multiple recent waves to find which one affects this point
    // We check up to uWavePeriod waves back
    float maxIntensity = 0.0;
    float numWavesToCheck = min(uWavePeriod, 10.0); // Limit to reasonable number
    
    // Wave width: controlled by amplitude (1-10)
    // Normalize amplitude: map from 1-10 to wave width (0.05 to 0.5 of maxDistance)
    float normalizedAmplitude = uWavesAmplitude * 0.05; // 0.05 to 0.5
    float waveWidth = uMaxDistance * normalizedAmplitude;
    
    for (float i = 0.0; i < numWavesToCheck; i += 1.0) {
        // Calculate wave number to check
        float waveNum = currentWaveNumber - i;
        
        // Only process if wave has started
        if (waveNum >= 0.0) {
            // Calculate start time for this wave
            float waveStartTime = waveNum * timeInterval;
            
            // Calculate how long this wave has been traveling
            float waveTravelTime = uTime - waveStartTime;
            
            // Only process if wave has started and hasn't traveled too far
            if (waveTravelTime >= 0.0) {
                // Calculate current position of this wave front (distance from center)
                float waveFrontDistance = waveTravelTime * normalizedSpeed;
                
                // Only process if wave is still within reasonable distance
                if (waveFrontDistance <= uMaxDistance * 1.5) {
                    // Calculate distance from this wave front
                    float distFromThisWave = abs(distance - waveFrontDistance);

                    // Calculate intensity for this wave
                    float waveIntensity = 1.0 - smoothstep(0.0, waveWidth, distFromThisWave);

                    // Lifecycle envelope: smooth fade-in at start, smooth
                    // fade-out at end. Without this each wave appears/disappears
                    // abruptly. waveProgress goes 0→1 across the wave's lifetime
                    // (= time for front to reach maxDistance * 1.5).
                    float waveLifetime = uMaxDistance * 1.5 / max(normalizedSpeed, 0.001);
                    float waveProgress = waveTravelTime / waveLifetime;
                    float fadeIn = smoothstep(0.0, 0.15, waveProgress);
                    float fadeOut = 1.0 - smoothstep(0.6, 1.0, waveProgress);
                    waveIntensity *= fadeIn * fadeOut;

                    // Take maximum intensity from all waves
                    maxIntensity = max(maxIntensity, waveIntensity);
                }
            }
        }
    }
    
    // Use maximum intensity found
    float intensity = maxIntensity;
    
    // Apply minimum distance threshold - no waves inside this radius
    if (distance < uMinDistance) {
        intensity = 0.0;
    }
    
    // Store intensity for fragment shader
    vWaveIntensity = intensity;
    
    // Store distance from center for appearance effect in fragment shader
    vDistanceFromCenter = distance;
    
    // Displace particles along selected axis based on wave intensity and displacement amount
    // uDisplacement is already scaled to model size (as percentage of maxDistance)
    // So we can use it directly without additional scaling
    
    // Calculate displacement: particles move along selected axis when wave passes
    float displacement = intensity * uDisplacement;
    
    // Apply displacement to position based on selected axis
    vec3 pos = position;
    if (uDisplacementAxis < 0.5) {
        // X axis (0)
        pos.x += displacement;
    } else if (uDisplacementAxis < 1.5) {
        // Y axis (1)
        pos.y += displacement;
    } else {
        // Z axis (2)
        pos.z += displacement;
    }
    
    // Use original world position for vWorldPosition (needed for reveal effect)
    vWorldPosition = worldPos;
    
    // Transform displaced position to clip space
    vec4 mvPosition = modelViewMatrix * vec4(pos, 1.0);
    gl_Position = projectionMatrix * mvPosition;

    // Size in pixels: either a constant (uSizeAttenuation == 0), or scaled
    // inversely with view-space depth so points appear "world-sized".
    // Mix between the two via uSizeAttenuation so it's a smooth toggle.
    float depth = max(-mvPosition.z, 0.001);
    float attenuated = uPointSize * uAttenuationScale / depth;
    gl_PointSize = mix(uPointSize, attenuated, clamp(uSizeAttenuation, 0.0, 1.0));
}
